import { create } from 'zustand';
import {
  CONTROLS_CONFIG,
  DungeonAction,
  INPUT_PRESETS,
  InputPresetId,
  InterfaceAction,
} from '../data/controls.config';
import {
  ActionMapping,
  GamepadBinding,
  KeyboardBinding,
} from '../types/controls.types';

export interface ControlsState {
  interface: Record<InterfaceAction, ActionMapping>;
  dungeon: Record<DungeonAction, ActionMapping>;
  mouseControlEnabled: boolean;
  inputPresetChosen: boolean;
}

const STORAGE_KEY = 'usagi_controls';
/**
 * Mapping schema version. Increment on every change to the default mappings
 * (new action, remapped binding) to invalidate persisted mappings and force
 * migration to the new defaults. NOTE: a user who customized their mappings
 * loses their overrides.
 */
const CONTROLS_VERSION = 4;

function buildDefaultMapping(): ControlsState {
  return {
    interface: Object.fromEntries(
      Object.entries(CONTROLS_CONFIG.interface).map(([action, config]) => [
        action,
        {
          keyboard: config.defaultKeyboard,
          gamepad: config.defaultGamepad,
        },
      ]),
    ) as Record<InterfaceAction, ActionMapping>,
    dungeon: Object.fromEntries(
      Object.entries(CONTROLS_CONFIG.dungeon).map(([action, config]) => [
        action,
        {
          keyboard: config.defaultKeyboard,
          gamepad: config.defaultGamepad,
        },
      ]),
    ) as Record<DungeonAction, ActionMapping>,
    mouseControlEnabled: false,
    inputPresetChosen: false,
  };
}

/**
 * If the persisted schema version differs from `CONTROLS_VERSION`, fall back to
 * the defaults. Otherwise, actions missing from the save are filled in from the
 * defaults.
 */
function loadFromStorage(): ControlsState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return buildDefaultMapping();
    }

    const parsed = JSON.parse(raw) as Partial<ControlsState> & {
      version?: number;
      mouseControlEnabled?: boolean;
      inputPresetChosen?: boolean;
    };

    // Stale schema → discard the old persisted mapping.
    if (parsed.version !== CONTROLS_VERSION) {
      return buildDefaultMapping();
    }

    const defaults = buildDefaultMapping();
    return {
      interface: Object.fromEntries(
        Object.keys(defaults.interface).map((action) => [
          action,
          parsed.interface?.[action as InterfaceAction] ??
            defaults.interface[action as InterfaceAction],
        ]),
      ) as Record<InterfaceAction, ActionMapping>,
      dungeon: Object.fromEntries(
        Object.keys(defaults.dungeon).map((action) => [
          action,
          parsed.dungeon?.[action as DungeonAction] ??
            defaults.dungeon[action as DungeonAction],
        ]),
      ) as Record<DungeonAction, ActionMapping>,
      mouseControlEnabled: parsed.mouseControlEnabled ?? false,
      inputPresetChosen: parsed.inputPresetChosen ?? false,
    };
  } catch {
    return buildDefaultMapping();
  }
}

function saveToStorage(state: ControlsState): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...state, version: CONTROLS_VERSION }),
    );
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded…)
  }
}

function keyboardBindingsEqual(
  a: KeyboardBinding,
  b: KeyboardBinding,
): boolean {
  if (a.code !== b.code) {
    return false;
  }
  const ma = a.modifiers ?? [];
  const mb = b.modifiers ?? [];
  if (ma.length !== mb.length) {
    return false;
  }
  for (let i = 0; i < ma.length; i++) {
    if (!mb.includes(ma[i])) {
      return false;
    }
  }
  return true;
}

function gamepadBindingsEqual(a: GamepadBinding, b: GamepadBinding): boolean {
  if (a.type !== b.type) {
    return false;
  }
  if (a.type === 'button' && b.type === 'button') {
    return a.index === b.index;
  }
  if (a.type === 'axis' && b.type === 'axis') {
    return a.axisIndex === b.axisIndex && a.direction === b.direction;
  }
  if (a.type === 'combo' && b.type === 'combo') {
    return a.modifier === b.modifier && a.button === b.button;
  }
  return false;
}

/**
 * Interface action pairs whose bindings must stay distinct — if a rebind
 * creates a conflict, the two are swapped automatically so each side keeps a
 * usable key (UX rule).
 */
const SWAP_PAIRS: ReadonlyArray<[InterfaceAction, InterfaceAction]> = [
  ['CONFIRM', 'CANCEL'],
];

function findSwapPartner(id: InterfaceAction): InterfaceAction | null {
  for (const [a, b] of SWAP_PAIRS) {
    if (a === id) {
      return b;
    }
    if (b === id) {
      return a;
    }
  }
  return null;
}

interface ControlsActions {
  setInterfaceKeyboard: (id: InterfaceAction, binding: KeyboardBinding) => void;
  setInterfaceGamepad: (id: InterfaceAction, binding: GamepadBinding) => void;
  setDungeonKeyboard: (id: DungeonAction, binding: KeyboardBinding) => void;
  setDungeonGamepad: (id: DungeonAction, binding: GamepadBinding) => void;
  resetAllControls: () => void;
  applyPreset: (presetId: InputPresetId) => void;
}

export const useControlsStore = create<ControlsState & ControlsActions>(
  (set) => ({
    ...loadFromStorage(),

    setInterfaceKeyboard: (id, binding) =>
      set((state) => {
        const partner = findSwapPartner(id);
        const conflict =
          partner &&
          keyboardBindingsEqual(state.interface[partner].keyboard, binding);
        const updated: Record<InterfaceAction, ActionMapping> = {
          ...state.interface,
          [id]: { ...state.interface[id], keyboard: binding },
        };
        if (conflict && partner) {
          updated[partner] = {
            ...state.interface[partner],
            keyboard: state.interface[id].keyboard,
          };
        }
        saveToStorage({ ...state, interface: updated });
        return { interface: updated };
      }),

    setInterfaceGamepad: (id, binding) =>
      set((state) => {
        const partner = findSwapPartner(id);
        const conflict =
          partner &&
          gamepadBindingsEqual(state.interface[partner].gamepad, binding);
        const updated: Record<InterfaceAction, ActionMapping> = {
          ...state.interface,
          [id]: { ...state.interface[id], gamepad: binding },
        };
        if (conflict && partner) {
          updated[partner] = {
            ...state.interface[partner],
            gamepad: state.interface[id].gamepad,
          };
        }
        saveToStorage({ ...state, interface: updated });
        return { interface: updated };
      }),

    setDungeonKeyboard: (id, binding) =>
      set((state) => {
        const next = {
          dungeon: {
            ...state.dungeon,
            [id]: { ...state.dungeon[id], keyboard: binding },
          },
        };
        saveToStorage({ ...state, dungeon: next.dungeon });
        return next;
      }),

    setDungeonGamepad: (id, binding) =>
      set((state) => {
        const next = {
          dungeon: {
            ...state.dungeon,
            [id]: { ...state.dungeon[id], gamepad: binding },
          },
        };
        saveToStorage({ ...state, dungeon: next.dungeon });
        return next;
      }),

    resetAllControls: () =>
      set((state) => {
        const defaults = buildDefaultMapping();
        const next = {
          interface: defaults.interface,
          dungeon: defaults.dungeon,
          mouseControlEnabled: false,
          inputPresetChosen: state.inputPresetChosen,
        };
        saveToStorage(next);
        return next;
      }),

    applyPreset: (presetId) =>
      set((state) => {
        const preset = INPUT_PRESETS[presetId];
        const dungeon = { ...state.dungeon } as Record<
          DungeonAction,
          ActionMapping
        >;
        (Object.keys(preset.dungeonKeyboard) as DungeonAction[]).forEach(
          (action) => {
            dungeon[action] = {
              ...dungeon[action],
              keyboard: preset.dungeonKeyboard[action],
            };
          },
        );
        const next = {
          interface: state.interface,
          dungeon,
          mouseControlEnabled: preset.mouseControl,
          inputPresetChosen: true,
        };
        saveToStorage(next);
        return next;
      }),
  }),
);
